import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CoreFields.module.css";
import { sanitizeText, sanitizeAttribute } from "../../../../../utils/sanitize";

// SSR-safe layout effect
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Custom hook for auto-resizing textarea
function useAutosizeTextarea(ref, value) {
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
}

const buildClassName = (...classes) => classes.filter(Boolean).join(" ");

export default function CoreFields({
  title,
  body,
  tags,
  errors,
  onUpdate,
  onBlur,
  aiOverlay = {},
}) {
  const { t } = useTranslation();
  const [tagInput, setTagInput] = useState("");
  const tagInputRef = useRef(null);
  const textareaRef = useRef(null);

  const overlayTitle = aiOverlay?.title;
  const overlayBody = aiOverlay?.body;
  const overlayTags = aiOverlay?.tags;
  const hasTitleOverlay = Boolean(overlayTitle);
  const hasBodyOverlay = Boolean(overlayBody);
  const hasTagsOverlay = Boolean(overlayTags);

  const displayedTitle = overlayTitle
    ? overlayTitle.isRemoval
      ? ""
      : String(overlayTitle.value ?? "")
    : String(title ?? "");

  const displayedBody = overlayBody
    ? overlayBody.isRemoval
      ? ""
      : String(overlayBody.value ?? "")
    : String(body ?? "");

  const displayedTags = overlayTags
    ? overlayTags.isRemoval
      ? []
      : Array.isArray(overlayTags.value)
        ? overlayTags.value
            .filter((tag) => typeof tag === "string" && tag.trim().length > 0)
            .map((tag) => tag.trim())
        : []
    : tags;

  const isClearingTags = overlayTags?.isRemoval === true;
  // Use the custom hook for auto-resizing based on displayed content
  useAutosizeTextarea(textareaRef, displayedBody);

  const scheduleFocusRestore = (element) => {
    if (!element || typeof element.focus !== "function") return;

    const focusElement = element;
    const restore = () => {
      focusElement.focus({ preventScroll: true });
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(restore);
    } else {
      setTimeout(restore, 0);
    }
  };

  const handleTitleChange = (e) => {
    if (hasTitleOverlay) return;
    onUpdate("title", e.target.value);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key !== "Enter") return;

    const isImeCommit =
      e.isComposing ||
      (e.nativeEvent && e.nativeEvent.isComposing) ||
      e.keyCode === 229;

    if (isImeCommit) return;

    e.preventDefault();
    e.stopPropagation();
    scheduleFocusRestore(e.currentTarget);
  };

  const handleBodyChange = (e) => {
    if (hasBodyOverlay) return;
    onUpdate("body", e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if (hasTagsOverlay) return;
    if (e.key === "Enter") {
      const isImeCommit =
        e.isComposing ||
        (e.nativeEvent && e.nativeEvent.isComposing) ||
        e.keyCode === 229;

      if (isImeCommit) return;

      const targetElement = e.currentTarget;

      e.preventDefault();
      e.stopPropagation();

      const trimmedInput = tagInput.trim();
      if (trimmedInput) {
        const newTag = trimmedInput.toLowerCase();
        if (!tags.includes(newTag)) {
          onUpdate("tags", [...tags, newTag]);
        }
        setTagInput("");
      }
      scheduleFocusRestore(tagInputRef.current || targetElement);
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      // Remove last tag if backspace pressed on empty input
      const newTags = [...tags];
      newTags.pop();
      onUpdate("tags", newTags);
    }
  };

  const removeTag = (index) => {
    if (hasTagsOverlay) return;
    const newTags = tags.filter((_, i) => i !== index);
    onUpdate("tags", newTags);
  };

  const focusTagInput = () => {
    tagInputRef.current?.focus();
  };

  return (
    <>
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="title">
          {t("records.editor.core.title_label", "Title")}
        </label>
        <input
          type="text"
          id="title"
          className={buildClassName(
            styles.input,
            errors["content.title"] ? styles.error : "",
            hasTitleOverlay ? styles.overlayValue : "",
            hasTitleOverlay ? styles.readOnlyInput : ""
          )}
          value={displayedTitle}
          onChange={handleTitleChange}
          onKeyDown={handleTitleKeyDown}
          onBlur={() => onBlur && onBlur("title", title)}
          placeholder={t("records.editor.core.title_placeholder", "What's this about?")}
          readOnly={hasTitleOverlay}
          aria-readonly={hasTitleOverlay}
          enterKeyHint="done"
        />
        {errors["content.title"] && (
          <span className={styles.errorMessage}>{errors["content.title"]}</span>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="body">
          {t("records.editor.core.content_label", "Content")}
        </label>
        <textarea
          ref={textareaRef}
          id="body"
          className={buildClassName(
            styles.textarea,
            errors.body ? styles.error : "",
            hasBodyOverlay ? styles.overlayValue : "",
            hasBodyOverlay ? styles.readOnlyInput : ""
          )}
          value={displayedBody}
          onChange={handleBodyChange}
          placeholder={t(
            "records.editor.core.content_placeholder",
            "Just type naturally... I'll understand and organize it for you"
          )}
          readOnly={hasBodyOverlay}
          aria-readonly={hasBodyOverlay}
        />
        {errors.body && (
          <span className={styles.errorMessage}>{errors.body}</span>
        )}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t("common.tags", "Tags")}</label>
        <div
          className={buildClassName(
            styles.tagContainer,
            hasTagsOverlay ? styles.tagContainerDisabled : "",
            hasTagsOverlay ? styles.overlayValue : ""
          )}
          onClick={focusTagInput}
        >
          {displayedTags.map((tag, index) => (
            <div key={`${tag}-${index}`} className={styles.tag}>
              <span>{sanitizeText(tag)}</span>
              <button
                type="button"
                className={styles.tagRemove}
                disabled={hasTagsOverlay}
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(index);
                }}
                aria-label={t("records.editor.core.remove_tag_aria", {
                  tag: sanitizeAttribute(tag),
                  defaultValue: `Remove tag ${sanitizeAttribute(tag)}`,
                })}
              >
                <svg width="14" height="14" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
          {isClearingTags && (
            <span className={styles.tagRemovalNote}>
              {t("records.editor.core.ai_clear_tags", "AI will clear all tags.")}
            </span>
          )}
          {!hasTagsOverlay && (
            <input
              ref={tagInputRef}
              type="text"
              className={styles.tagInput}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              enterKeyHint="done"
              placeholder={
                displayedTags.length === 0
                  ? t("records.editor.core.tags_placeholder", "Add tag...")
                  : ""
              }
            />
          )}
          {hasTagsOverlay && displayedTags.length === 0 && !isClearingTags && (
            <span className={styles.tagPlaceholder}>
              {t("records.editor.core.no_tags", "No tags")}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
